/**
 * Golden Path Form Demo
 *
 * A 4-step business partner application that demonstrates the recommended
 * pattern for all forms in this codebase:
 *
 *   Controller + Field/FieldLabel/FieldError + Zod resolver
 *
 * Key patterns shown:
 *   - Single useForm instance shared across steps via prop
 *   - Step-boundary validation with form.trigger(stepFields)
 *   - Cross-field validation via schema.superRefine (email match)
 *   - Conditional required fields via superRefine (EIN by entity type)
 *   - Custom formatted inputs (Phone, EIN, DatePicker) via ControllerRenderProps
 *   - Value-controlled inputs (UsZipInput, UsStateSelect) via field.value/onChange
 *   - RadioGroup with styled card tiles
 *   - Textarea with live character counter
 *   - Checkbox with styled container
 *   - Read-only review step
 *   - aria-invalid threaded to every input for accessible error styling
 */

import { valibotResolver } from "@hookform/resolvers/valibot";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as v from "valibot";

import { Button } from "@/web/components/primitives/button";
import { Checkbox } from "@/web/components/primitives/checkbox";
import { Input } from "@/web/components/primitives/input";
import { RadioGroup } from "@/web/components/primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/web/components/primitives/select";
import { Textarea } from "@/web/components/primitives/textarea";
import { DatePickerInput } from "@/web/components/styled/DatePickerInput";
import { EinInput } from "@/web/components/styled/EinInput";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldRow,
  FieldSection,
  LabeledRadioGroupItem,
} from "@/web/components/styled/Field";
import { PhoneInput } from "@/web/components/styled/PhoneInput";
import { UsStateSelect } from "@/web/components/styled/UsStateSelect";
import { UsZipInput } from "@/web/components/styled/UsZipInput";
import { validateDateOfBirth } from "@/web/lib/datetime";
import { cn } from "@/web/lib/styling";

// ─── Schema ────────────────────────────────────────────────────────────────────

const ENTITY_TYPES = ["llc", "corp", "partnership", "sole_prop"] as const;
const INDUSTRIES = ["real_estate", "finance", "technology", "other"] as const;

const schema = v.pipe(
  v.object({
    // Step 1 — Contact
    firstName: v.pipe(v.string(), v.minLength(1, "Required")),
    lastName: v.pipe(v.string(), v.minLength(1, "Required")),
    email: v.pipe(v.string(), v.email("Enter a valid email address")),
    confirmEmail: v.pipe(v.string(), v.email("Enter a valid email address")),
    phone: v.pipe(v.string(), v.minLength(14, "Enter a complete 10-digit phone number")),
    dateOfBirth: v.pipe(
      v.string(),
      v.rawCheck(({ dataset, addIssue }) => {
        if (!dataset.typed) return;
        const error = validateDateOfBirth(dataset.value);
        if (error !== null) {
          addIssue({ message: error });
        }
      }),
    ),

    // Step 2 — Business
    businessName: v.pipe(v.string(), v.minLength(2, "Business name must be at least 2 characters")),
    entityType: v.picklist(ENTITY_TYPES, "Select an entity type"),
    ein: v.optional(v.string()),
    industry: v.picklist(INDUSTRIES, "Select an industry"),
    foundedDate: v.pipe(v.string(), v.minLength(10, "Enter a valid date (MM/DD/YYYY)")),
    description: v.pipe(
      v.string(),
      v.minLength(20, "Description must be at least 20 characters"),
      v.maxLength(300, "Description must be 300 characters or fewer"),
    ),

    // Step 3 — Address
    street: v.pipe(v.string(), v.minLength(1, "Required")),
    suite: v.optional(v.string()),
    city: v.pipe(v.string(), v.minLength(1, "Required")),
    state: v.pipe(v.string(), v.minLength(2, "Select a state")),
    zip: v.pipe(v.string(), v.minLength(5, "Enter a valid ZIP code")),

    // Step 4 — Confirm
    agreedToTerms: v.pipe(
      v.boolean(),
      v.check((val) => val === true, "You must agree to the terms to continue"),
    ),
  }),
  v.rawCheck(({ dataset, addIssue }) => {
    if (!dataset.typed) return;
    const data = dataset.value;
    if (data.email !== data.confirmEmail) {
      addIssue({
        message: "Emails do not match",
        path: [
          {
            type: "object",
            origin: "value",
            input: dataset.value,
            key: "confirmEmail",
            value: dataset.value.confirmEmail,
          },
        ],
      });
    }
  }),
  v.rawCheck(({ dataset, addIssue }) => {
    if (!dataset.typed) return;
    const data = dataset.value;
    if (data.entityType !== "sole_prop" && (!data.ein || data.ein.length < 10)) {
      addIssue({
        message: "EIN is required for this entity type (e.g. 12-3456789)",
        path: [
          {
            type: "object",
            origin: "value",
            input: dataset.value,
            key: "ein",
            value: dataset.value.ein,
          },
        ],
      });
    }
  }),
);

type FormValues = v.InferOutput<typeof schema>;

// Fields validated at each step boundary before advancing
const STEP_FIELDS: Record<number, (keyof FormValues)[]> = {
  1: ["firstName", "lastName", "email", "confirmEmail", "phone", "dateOfBirth"],
  2: ["businessName", "entityType", "ein", "industry", "foundedDate", "description"],
  3: ["street", "suite", "city", "state", "zip"],
  4: ["agreedToTerms"],
};

// ─── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_protected/admin/styling/form/")({
  component: RouteComponent,
});

// ─── Step Indicator ────────────────────────────────────────────────────────────

const STEP_LABELS = ["Contact", "Business", "Address", "Review"];

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <nav aria-label="Form progress" className="flex items-center justify-center">
      {STEP_LABELS.map((label, i) => {
        const step = i + 1;
        const isDone = step < currentStep;
        const isActive = step === currentStep;
        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "flex size-7 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors",
                  isDone && "border-primary bg-primary text-primary-foreground",
                  isActive && "border-primary bg-background text-primary",
                  !isDone && !isActive && "border-muted-foreground/30 text-muted-foreground",
                )}
              >
                {isDone ? <CheckCircle2Icon className="size-3.5" /> : step}
              </div>
              <span
                className={cn(
                  "text-xs",
                  isActive ? "font-semibold text-foreground" : "text-muted-foreground",
                )}
              >
                {label}
              </span>
            </div>
            {i < totalSteps - 1 && (
              <div
                className={cn(
                  "mb-5 h-px w-10 transition-colors sm:w-16",
                  step < currentStep ? "bg-primary" : "bg-muted-foreground/20",
                )}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ─── Step 1: Contact Info ──────────────────────────────────────────────────────

function Step1({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const { control } = form;
  return (
    <FieldGroup>
      <FieldSection title="Your name">
        <FieldRow>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="firstName">First name</FieldLabel>
                <Input
                  {...field}
                  id="firstName"
                  placeholder="Jane"
                  autoComplete="given-name"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="lastName">Last name</FieldLabel>
                <Input
                  {...field}
                  id="lastName"
                  placeholder="Smith"
                  autoComplete="family-name"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>
      </FieldSection>

      <FieldSection
        title="Contact details"
        description="We'll use your email to send application updates."
      >
        {/*
         * Cross-field validation demo: confirmEmail is validated against email
         * via schema.superRefine. Both fields share the same form instance so
         * when email changes, confirmEmail re-validates automatically.
         */}
        <FieldRow>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="email">Email address</FieldLabel>
                <Input
                  {...field}
                  id="email"
                  type="email"
                  placeholder="jane@example.com"
                  autoComplete="email"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            name="confirmEmail"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="confirmEmail">Confirm email</FieldLabel>
                <Input
                  {...field}
                  id="confirmEmail"
                  type="email"
                  placeholder="jane@example.com"
                  autoComplete="off"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>

        {/*
         * Custom formatted input demo: PhoneInput accepts ControllerRenderProps
         * directly. aria-invalid is passed through its ...slotProps spread to
         * the underlying <Input> so the red border styling still applies.
         */}
        <FieldRow>
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="phone">Phone number</FieldLabel>
                <PhoneInput field={field} aria-invalid={fieldState.invalid} id="phone" />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          {/*
           * DatePickerInput wraps its own <Input> internally and doesn't
           * forward aria-invalid — error state is communicated via FieldLabel
           * color and FieldError below instead.
           */}
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Date of birth</FieldLabel>
                <DatePickerInput field={field} />
                <FieldDescription>Must be 18 or older to apply.</FieldDescription>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>
      </FieldSection>
    </FieldGroup>
  );
}

// ─── Step 2: Business Details ──────────────────────────────────────────────────

function Step2({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const { control, watch } = form;
  const entityType = watch("entityType");
  const description = watch("description") ?? "";

  return (
    <FieldGroup>
      <FieldSection
        title="Entity information"
        description="Legal details about your business entity."
      >
        <FieldRow>
          <Controller
            name="businessName"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="businessName">Legal business name</FieldLabel>
                <Input
                  {...field}
                  id="businessName"
                  placeholder="Acme Investments LLC"
                  autoComplete="organization"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            name="entityType"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="entityType">Entity type</FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="entityType"
                    className="w-full"
                    aria-invalid={fieldState.invalid}
                  >
                    <SelectValue placeholder="Select type…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="llc">LLC</SelectItem>
                    <SelectItem value="corp">Corporation</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="sole_prop">Sole Proprietorship</SelectItem>
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>

        {/*
         * Conditional required field demo: EIN is required unless entity type
         * is sole_prop. The schema.superRefine rule enforces this. The UI
         * reacts to the watched entityType to update the label and description
         * text in real time.
         */}
        <FieldRow>
          <Controller
            name="ein"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="ein">
                  EIN
                  {entityType === "sole_prop" && (
                    <span className="ml-1 text-xs leading-none font-normal text-muted-foreground">
                      (optional)
                    </span>
                  )}
                </FieldLabel>
                <EinInput field={field} id="ein" aria-invalid={fieldState.invalid} />
                <FieldDescription>
                  {entityType === "sole_prop"
                    ? "Optional for sole proprietors."
                    : "Required — 9-digit federal employer tax ID."}
                </FieldDescription>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            name="foundedDate"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Date founded</FieldLabel>
                <DatePickerInput field={field} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>
      </FieldSection>

      {/*
       * RadioGroup with styled card tiles demo: each option is a full-width
       * clickable label containing the RadioGroupItem. Clicking anywhere on
       * the card selects the option.
       */}
      <FieldSection title="Primary industry">
        <Controller
          name="industry"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="grid grid-cols-2 gap-2 sm:grid-cols-4"
              >
                {(
                  [
                    { value: "real_estate", label: "Real Estate" },
                    { value: "finance", label: "Finance" },
                    { value: "technology", label: "Technology" },
                    { value: "other", label: "Other" },
                  ] as const
                ).map(({ value, label }) => (
                  <LabeledRadioGroupItem
                    key={value}
                    id={`industry-${value}`}
                    value={value}
                    checked={field.value === value}
                    invalid={fieldState.invalid}
                  >
                    {label}
                  </LabeledRadioGroupItem>
                ))}
              </RadioGroup>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </FieldSection>

      {/*
       * Textarea with live character counter demo: watch() reads the current
       * value to drive the counter display without triggering re-renders of
       * the entire form.
       */}
      <FieldSection title="About your business">
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <div className="flex items-baseline justify-between">
                <FieldLabel htmlFor="description">Business description</FieldLabel>
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    description.length > 280 ? "text-destructive" : "text-muted-foreground",
                  )}
                >
                  {description.length}/300
                </span>
              </div>
              <Textarea
                {...field}
                id="description"
                rows={4}
                placeholder="Describe what your business does, its focus, and what makes it unique…"
                className="resize-none"
                aria-invalid={fieldState.invalid}
              />
              <FieldDescription>Min 20 characters. Shown to prospective partners.</FieldDescription>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </FieldSection>
    </FieldGroup>
  );
}

// ─── Step 3: Address ───────────────────────────────────────────────────────────

function Step3({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const { control } = form;
  return (
    <FieldGroup>
      <FieldSection title="Business address" description="Principal place of business.">
        <Controller
          name="street"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="street">Street address</FieldLabel>
              <Input
                {...field}
                id="street"
                placeholder="123 Main St"
                autoComplete="street-address"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <Controller
          name="suite"
          control={control}
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="suite">
                Suite / Unit
                <span className="ml-1 text-xs leading-none font-normal text-muted-foreground">
                  (optional)
                </span>
              </FieldLabel>
              <Input {...field} id="suite" placeholder="Suite 400" autoComplete="address-line2" />
            </Field>
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="city">City</FieldLabel>
              <Input
                {...field}
                id="city"
                placeholder="New York"
                autoComplete="address-level2"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        {/*
         * Value-controlled inputs demo: UsStateSelect and UsZipInput accept
         * plain value/onChange props rather than ControllerRenderProps.
         * Wire them up by reading field.value and calling field.onChange.
         */}
        <FieldRow>
          <Controller
            name="state"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="state">State</FieldLabel>
                <UsStateSelect id="state" value={field.value} onChange={field.onChange} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            name="zip"
            control={control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="zip">ZIP code</FieldLabel>
                <UsZipInput id="zip" value={field.value} onChange={field.onChange} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </FieldRow>
      </FieldSection>
    </FieldGroup>
  );
}

// ─── Step 4: Review & Confirm ──────────────────────────────────────────────────

function ReviewRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value || "—"}</dd>
    </div>
  );
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  llc: "LLC",
  corp: "Corporation",
  partnership: "Partnership",
  sole_prop: "Sole Proprietorship",
};

const INDUSTRY_LABELS: Record<string, string> = {
  real_estate: "Real Estate",
  finance: "Finance",
  technology: "Technology",
  other: "Other",
};

function Step4({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const { control, getValues } = form;
  const values = getValues();

  return (
    <FieldGroup>
      <FieldSection
        title="Review your application"
        description="Please verify everything looks correct before submitting."
      >
        <div className="flex flex-col gap-3">
          <div className="rounded-none border bg-white p-4">
            <p className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Contact
            </p>
            <dl className="flex flex-col gap-2">
              <ReviewRow label="Name" value={`${values.firstName} ${values.lastName}`} />
              <ReviewRow label="Email" value={values.email} />
              <ReviewRow label="Phone" value={values.phone} />
              <ReviewRow label="Date of birth" value={values.dateOfBirth} />
            </dl>
          </div>

          <div className="rounded-none border bg-white p-4">
            <p className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Business
            </p>
            <dl className="flex flex-col gap-2">
              <ReviewRow label="Business name" value={values.businessName} />
              <ReviewRow label="Entity type" value={ENTITY_TYPE_LABELS[values.entityType]} />
              <ReviewRow label="EIN" value={values.ein} />
              <ReviewRow label="Industry" value={INDUSTRY_LABELS[values.industry]} />
              <ReviewRow label="Founded" value={values.foundedDate} />
              <ReviewRow label="Description" value={values.description} />
            </dl>
          </div>

          <div className="rounded-none border bg-white p-4">
            <p className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Address
            </p>
            <dl className="flex flex-col gap-2">
              <ReviewRow
                label="Street"
                value={[values.street, values.suite].filter(Boolean).join(", ")}
              />
              <ReviewRow label="City" value={values.city} />
              <ReviewRow label="State" value={values.state} />
              <ReviewRow label="ZIP" value={values.zip} />
            </dl>
          </div>
        </div>
      </FieldSection>

      {/*
       * Checkbox with styled container demo: the wrapping <label> acts as
       * the click target for the entire card. Border and bg change to reflect
       * the checked/error states without any extra state.
       */}
      <FieldSection title="Terms & conditions">
        <Controller
          name="agreedToTerms"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <label
                htmlFor="agreedToTerms"
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-none border bg-white p-4 text-sm transition-colors",
                  "hover:bg-accent",
                  field.value && "border-primary bg-primary/5",
                  fieldState.invalid && "border-destructive",
                )}
              >
                <Checkbox
                  id="agreedToTerms"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                  aria-invalid={fieldState.invalid}
                  className="mt-0.5 shrink-0"
                />
                <span className="leading-relaxed">
                  I confirm that the information provided is accurate and I agree to the{" "}
                  <span className="font-semibold">Terms of Service</span> and{" "}
                  <span className="font-semibold">Privacy Policy</span>.
                </span>
              </label>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </FieldSection>
    </FieldGroup>
  );
}

// ─── Root Component ────────────────────────────────────────────────────────────

function RouteComponent() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const totalSteps = 4;

  /*
   * Single useForm instance shared across all steps via prop.
   * mode: "onTouched" — fields don't error on first render; once touched,
   * they validate in real time. form.trigger() on step advance forces
   * validation of all current-step fields regardless of touch state.
   */
  const form = useForm<FormValues>({
    resolver: valibotResolver(schema),
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      phone: "",
      dateOfBirth: "",
      businessName: "",
      entityType: undefined,
      ein: "",
      industry: undefined,
      foundedDate: "",
      description: "",
      street: "",
      suite: "",
      city: "",
      state: "",
      zip: "",
      agreedToTerms: false,
    },
  });

  const advance = async () => {
    const fields = STEP_FIELDS[step];
    const valid = await form.trigger(fields);

    if (valid) setStep((s) => s + 1);
  };

  const onSubmit = (_data: FormValues) => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2Icon className="size-7 text-primary" />
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold">Application submitted</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We'll review your application and be in touch shortly.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setSubmitted(false);
            setStep(1);
            form.reset();
          }}
        >
          Start over
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-8 p-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">Business Partner Application</h1>
        <p className="text-sm text-muted-foreground">
          Golden path form — multi-step, Zod validation, custom inputs
        </p>
      </div>

      <StepIndicator currentStep={step} totalSteps={totalSteps} />

      <form onSubmit={form.handleSubmit(onSubmit)}>
        {step === 1 && <Step1 form={form} />}
        {step === 2 && <Step2 form={form} />}
        {step === 3 && <Step3 form={form} />}
        {step === 4 && <Step4 form={form} />}

        <div className="mt-8 flex justify-between">
          {step > 1 ? (
            <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)}>
              <ChevronLeftIcon />
              Back
            </Button>
          ) : (
            <div />
          )}

          {step < totalSteps ? (
            <Button type="button" onClick={advance}>
              Continue
              <ChevronRightIcon />
            </Button>
          ) : (
            <Button type="submit" isLoading={form.formState.isSubmitting}>
              Submit application
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
